import _ from 'lodash';
import { identity, isEqual, merge as lodashMerge } from 'lodash';
import { EMPTY, from, merge, Observable, of, throwError, zip } from 'rxjs';
import {
    catchError,
    concatMap,
    delayWhen,
    distinctUntilChanged,
    first,
    last,
    map,
    pluck,
    switchMap,
    tap,
} from 'rxjs/operators';

import { SEARCH_TITLE_TYPES } from '@skinsoft/const';
import {
    SearchResponse,
    isSearchResponse,
    MediaType,
    AssociatedMedias,
    DataStoreOptions,
    PushResult,
    SearchQuery,
    SearchTitleOptions,
    AssociatedMediasResource,
    TitleSearchType,
} from '@skinsoft/types'
import { CREATE_PATTERNS, CreatePattern } from '@skinsoft/const'
import { userSelectors } from '@skinsoft/selectors'
import { fetch, DEFAULT_PROXY } from '@skinsoft/network'
import { AuthService } from '@skinsoft/auth/src/service'
import { OfflineDataFetcher, OfflineDataUpdateCacher } from '@skinsoft/offline'
import { getCurrentDateAsSkinDate } from '@skinsoft/utils'
import { ImageTransferHelper, MediaTransferHelper } from '@skinsoft/media-transfer-helper'
import { FailedToUpdateFirstMediaError } from './errors';

type FragmentParser<T> = (response: any) => T[];

declare const config
const FALLBACK_LOCALE = config.fallback_locale

const MONGO_ID_REGEX = /^[a-f\d]{24}$/i

/**
 * Base class for all data management in the app.
 * Generics : 
 *  - T : principal type of the entity managed by this class
 *  - S : secondary type retrieved when performing a search query (if not specified, it is equals to T by default)
 */
export abstract class DataStore<T extends { id: string }, S extends { id: string } = T> {
    public userSelectors = userSelectors;

    public get sanitizedIdentifer() {
        // For a strange reason, 'replaceAll' doesn't exist in Chrome for now, so we cheat
        const sanitized = this.endpoint.split('/').join('');
        return sanitized
    }

    public get getNbEntitiesInUpdateCache() {
        return this.nbEntitiesInUpdateCache
    }

    protected proxy = DEFAULT_PROXY;
    protected endpoint: string;
    protected createPattern: CreatePattern;

    //// OFFLINE //////////////////////////////////
    protected offlineDataFetcher: OfflineDataFetcher<T, S>;
    protected offlineDataUpdateCacher: OfflineDataUpdateCacher<T>;
    protected emulateSearch: boolean = false;

    // Stats
    private fragmentParser: FragmentParser<T>;
    private userGroupId: string;
    protected typesForSearch: string[]

    // Offline mode 
    private globalPushing = false;

    private dataStore: DataStore<T>;
    private pushing: boolean = false;
    private pushresult: PushResult = { nbPushSuccesses: 0, nbToPush: 0 };
    private nbEntitiesInUpdateCache: number = 0;
    private nbCreatedEntitiesInUpdateCache: number = 0;

    private lastSynchroStats: any = null;
    private lastPushDate: number = 0;

    constructor(
        endpoint: string,
        typesForSearch?: string[],
        fragmentParser: FragmentParser<T> = identity,
        proxy = DEFAULT_PROXY,
        createPattern: CreatePattern = CREATE_PATTERNS.CREATE_THEN_UPDATE,
    ) {
        this.fragmentParser = fragmentParser;
        this.endpoint = endpoint;
        this.proxy = proxy;
        this.createPattern = createPattern;
        if (typesForSearch) {
            this.typesForSearch = typesForSearch
        }

        // Fetching user group
        AuthService.getInstance().user$.pipe(
            switchMap((userAuthResult) => {
                if (userAuthResult?.user) {
                    let currentWorkspaceId: string | null = localStorage.getItem("workspace")
                    if(!currentWorkspaceId) {
                        const defaultWorkspace = userSelectors.defaultWorkspace(userAuthResult.user)
                        const defaultWorkspaceId = defaultWorkspace?.workspaceId ?? defaultWorkspace?.id
                        if(defaultWorkspaceId) {
                            currentWorkspaceId = defaultWorkspaceId
                        }
                    }

                    return this.proxy.fetch("/groups/userGroup", {
                        userId: userAuthResult.user.id,
                        workspace: currentWorkspaceId,
                    })
                } else {
                    return EMPTY;
                }
            })
        ).subscribe((group) => {
            this.userGroupId = group.id;
        });

        // Offline mode
        this.initializeDataFetcher();
        this.initializeDataPusher();

    }

    /**
     * @deprecated This method fetches all data of one type. This can lead to performance issues 
     * or crash if data is very numerous. The api endpoint will probably be removed in future.
     * @param options 
     * @param method 
     */
    public fetchAll(options: DataStoreOptions = {}, method = 'GET'): Observable<T[]> {

        const searchParams = new URLSearchParams(options.query);
        searchParams.set('archived', 'false');

        const endpoint = options.endpoint || this.endpoint;
        const cacheKey = `${endpoint}?${searchParams.toString()}`;

        return merge(
            this.proxy.fetch(endpoint, method === 'GET' ? searchParams : undefined, method, method !== 'GET' ? options.query : undefined).pipe(
                map(this.fragmentParser),
                // tap(data => this.cache.write(cacheKey, data)),
                catchError(error => (fetch.isNetworkError(error) ? EMPTY : throwError(error))),
            ),
        ).pipe(distinctUntilChanged(isEqual));
    }

    public fetchOne(id: string, networkFirst = false): Observable<T> {
        // if online
        return from(this.hasCachedUpdates(id)).pipe(
            switchMap(hasCachedUpdates => {
                if (window.navigator.onLine && !hasCachedUpdates) {
                    const uri = `${this.endpoint}/${id}`;
        
                    const network$ = this.proxy.fetch(uri, undefined, undefined, undefined, networkFirst).pipe(
                        last(),
                        tap(data => {
                            if(this.offlineDataFetcher) {
                                this.offlineDataFetcher.updateOne(data.id, data)
                            }
                        }),
                        catchError(error => (fetch.isNetworkError(error) ? EMPTY : throwError(error))),
                    );
                    let source$ = network$;
        
                    if (networkFirst) {
                        source$ = source$.pipe(delayWhen(() => network$));
                    }
        
                    return source$.pipe(
                        distinctUntilChanged(isEqual),
                        this.mergeUpdateCacheDataToOneResult.bind(this),
                    );
                }
                // if offline
                else {
                    return this.fetchOneOfflineMode(id);
                }
            }),
        )
    }

    public fetchOneOnlineMode(id: string): Observable<T> {
        const theRealThis = this
        const uri = `${this.endpoint}/${id}`;
        return this.proxy.fetch(uri, undefined, undefined, undefined).pipe(
            last(),
            tap(data => {
                if(theRealThis.offlineDataFetcher) {
                    theRealThis.offlineDataFetcher.updateOne(data.id, data)
                }
            }),
            catchError(error => (fetch.isNetworkError(error) ? EMPTY : throwError(error))),
        );
    }

    public fetchOneOfflineMode(id: string): Observable<T> {
        return from(this.hasCachedUpdates(id)).pipe(
            switchMap(hasCachedUpdates => {
                if(hasCachedUpdates) {
                    return this.offlineDataUpdateCacher.fetchOneFromId(id)
                }
                return this.offlineDataFetcher.fetchOneFromId(id)
            }),
            switchMap(async (data: any) => {
                let result = data
                // merging values from update-cache with value from fetch-cache not to loose information
                let cachedUpdatedValue: Partial<T> | undefined
                if(this.offlineDataUpdateCacher) {
                    cachedUpdatedValue = await this.offlineDataUpdateCacher.getCachedValue(id)
                }
                if(cachedUpdatedValue) {
                    result = lodashMerge(data, cachedUpdatedValue)
                }
                return result
            }),
        )
    }

    public fetchMany(ids: string[]): Observable<SearchResponse<S>> {
        return this.searchAll({
            filteredIds: ids
        })
    }

    public paginatedSearch(searchQuery: SearchQuery, options: DataStoreOptions = {}): Observable<SearchResponse<S>> {
        const query: any = {...searchQuery}
        if(query.searchOnArchived === undefined) {
            query.searchOnArchived = false
        }
        if(query.searchOnVersioned === undefined) {
            query.searchOnVersioned = false
        }

        if(!searchQuery.criteriaTypes) {
            query.criteriaTypes = []
        }
        
        const endpoint = '/indexSearch/paginated-search'
        return this.proxy.fetch(endpoint, undefined, 'POST', query).pipe(
            catchError(error => (fetch.isNetworkError(error) ? EMPTY : throwError(error))),
            switchMap(response => {
                if(response.data && response.totalSize >= 0) {
                    let searchResponse: SearchResponse<S> = {
                        allData: response.data,
                        results: response.data,
                        totalSize: response.totalSize,
                    }

                    return of(searchResponse)
                }

                return throwError(new Error())
            })
        )
    }

    /**
     * 
     * @param queryText 
     * @param sortObject 
     * @param pageSize 
     * @param types 
     * @param page 
     * @param advancedSearches 
     * @param filteredIds 
     * @param pathAndValueToCheck 
     * @param searchEndpointOverride 
     * @param options 
     * @param excludedIds 
     * @param onlyOnLocallyCreated 
     * @param forceSearchTextId When queryText value is an id (mongoDb), setting to true will include the entities referencing the id in addition to the entity corresponding to the id
     * @param language
     * @returns 
     */
    public searchAll(searchQuery: SearchQuery, pathAndValueToCheck?: any, searchEndpointOverride: string | null = null, 
        options: DataStoreOptions = {}, onlyOnLocallyCreated: boolean = false, forceSearchTextId: boolean = false,
        excludeResultsFromCacheUp: boolean = false)
        : Observable<SearchResponse<S>> {

        if(!searchQuery.pageSize) searchQuery.pageSize = 15
        if(!searchQuery.page) searchQuery.page = 0
        if(!searchQuery.language) searchQuery.language = FALLBACK_LOCALE

        // if online
        if (window.navigator.onLine) {
            return this.searchAllOnline(searchQuery, pathAndValueToCheck, searchEndpointOverride, options, forceSearchTextId, excludeResultsFromCacheUp)
        } else {
            return this.searchAllOffline(searchQuery, pathAndValueToCheck, searchEndpointOverride, options, onlyOnLocallyCreated)
        }
    }

    /**
     * 
     * @param queryText 
     * @param sortObject 
     * @param pageSize 
     * @param types 
     * @param page 
     * @param advancedSearches 
     * @param filteredIds 
     * @param pathAndValueToCheck 
     * @param searchEndpointOverride 
     * @param options 
     * @param excludedIds 
     * @param forceSearchTextId When queryText value is an id (mongoDb), setting to true will include the entities referencing the id in addition to the entity corresponding to the id
     * @param language
     * @returns 
     */
    public searchAllOnline(searchQuery: SearchQuery, pathAndValueToCheck?: any, searchEndpointOverride: string | null = null, 
        options: DataStoreOptions = {}, forceSearchTextId: boolean = false, excludeResultsFromCacheUp: boolean = false)
        : Observable<SearchResponse<S>>  {
        
        if(!searchQuery.pageSize) searchQuery.pageSize = 15
        if(!searchQuery.page) searchQuery.page = 0
        if(!searchQuery.language) searchQuery.language = FALLBACK_LOCALE
        if(!searchQuery.criteriaTypes) {
            searchQuery.criteriaTypes = []
        }

        let fetchId: boolean = false
        // allow search by id (search by id doesn't work in query text)
        if(searchQuery.queryText && searchQuery.queryText.match(MONGO_ID_REGEX)) {
            if(forceSearchTextId) {
                fetchId = true
            } else {
                searchQuery.filteredIds ? searchQuery.filteredIds.push(searchQuery.queryText) : searchQuery.filteredIds = [searchQuery.queryText]
                searchQuery.queryText = ''
            }
        }

        const endpoint = searchEndpointOverride || options.endpoint || "/indexSearch/search";
        const searchTypes = searchQuery.types || this.typesForSearch // trying to get type name from endpoint (as they are suposed to be similar)
        const query = {
            queryText: searchQuery.queryText,
            sortObject: searchQuery.sortObject,
            types: searchTypes,
            advancedSearches: searchQuery.advancedSearches,
            excludedIds: searchQuery.excludedIds,
            filteredIds: searchQuery.filteredIds,
            group: this.userGroupId,
            language: searchQuery.language,
            page: searchQuery.page,
            pageSize: searchQuery.pageSize,
            pathAndValueToCheck,
            searchOnArchived: false,
            searchOnVersioned: false,
            criteriaTypes: [],
        }
        const cacheKey = `${endpoint}?${searchTypes.toString()}?${query.toString()}`;
        
        const defaultRequest$ = this.proxy.fetch(endpoint, undefined, 'POST', query).pipe(
            catchError(error => (fetch.isNetworkError(error) ? EMPTY : throwError(error))),
        )
        let request$ = defaultRequest$

        if(fetchId) {
            // include the entity with the given id in results
            request$ = this.fetchOne(searchQuery.queryText!).pipe(
                catchError(() => {
                    return defaultRequest$
                }),
                first(),
                switchMap(response => {
                    if(response && !isSearchResponse(response)) {
                        return zip(
                            of([response]),
                            defaultRequest$
                        )
                    }

                    return zip(of([]), defaultRequest$)
                }),
                switchMap(([resultFetchOne, resultDefaultRequest]) => {
                    let allData = resultDefaultRequest.allData ? resultFetchOne.concat(resultDefaultRequest.allData) : resultFetchOne
                    let results = resultDefaultRequest.results ? resultFetchOne.concat(resultDefaultRequest.results) : resultFetchOne

                    // remove eventual doubles
                    allData = _.uniqWith(allData, (entity1, entity2) => entity1.id === entity2.id)
                    results = _.uniqWith(results, (entity1, entity2) => entity1.id === entity2.id)

                    return of ({
                        totalSize: resultDefaultRequest.totalSize + resultFetchOne.length,
                        allData,
                        results,
                    })
                }),
            )
        }

        if(excludeResultsFromCacheUp) {
            return request$
        } else {
            
            return request$.pipe(
                this.mergeUpdateCacheDataToSearchResults.bind(this),
                // Adding locally created entities to results
                switchMap( // TODO can be improved with sorting cached values and online values together
                    async data => {
                        let localEntities = await this.getLocallyCreatedCachedObjectsAsS();
                        if (localEntities.length > 0) {
                            if (!data.allData) {
                                data.allData = []
                            }
    
                            // Restraining to filtered ids if there are some
                            if (searchQuery.filteredIds) {
                                const matchingLocalEntities = await this.offlineDataFetcher.searchAll(query, localEntities, await this.getLocallyUpdatedCachedObjectsAsS(), true).pipe(first()).toPromise();
                                localEntities = matchingLocalEntities.allData;
                            }
    
                            data.totalSize += localEntities.length;
                            data.allData = await this.offlineDataFetcher.insertLocallyCreatedDataInOnlineSearchResult(
                                data.allData,
                                localEntities, 
                                query, 
                                query.language ?? FALLBACK_LOCALE
                            )
                        }
                        return data 
                    }
                )
            )
        }

    }

    public searchAllOffline(searchQuery: SearchQuery, pathAndValueToCheck?: any, searchEndpointOverride: string | null = null, 
        options: DataStoreOptions = {}, onlyOnLocallyCreated: boolean = false, excludeResultsFromCacheUp: boolean = false)
        : Observable<SearchResponse<S>>  {
        
        if(!searchQuery.pageSize) searchQuery.pageSize = 15
        if(!searchQuery.page) searchQuery.page = 0
        if(!searchQuery.language) searchQuery.language = FALLBACK_LOCALE
        
        const searchTypes = searchQuery.types || this.typesForSearch
        const query = {
            queryText: searchQuery.queryText,
            sortObject: searchQuery.sortObject,
            types: searchTypes,

            advancedSearches: searchQuery.advancedSearches,
            excludedIds: searchQuery.excludedIds,
            filteredIds: searchQuery.filteredIds,
            group: this.userGroupId,
            language: searchQuery.language,
            page: searchQuery.page,
            pageSize: searchQuery.pageSize,
            pathAndValueToCheck,
            searchOnArchived: false,
            searchOnVersioned: false
        }

        if(searchQuery.queryText && searchQuery.queryText.match(MONGO_ID_REGEX)) {
            query.queryText = ""
            if(query.filteredIds) {
                query.filteredIds.push(searchQuery.queryText)
            } else {
                query.filteredIds = [searchQuery.queryText]
            }
        }

        return this.locallyCreatedEntitiesToData$()
                .pipe(
                    switchMap(
                        async locallyCreatedData => {
                            let locallyUpdatedData: S[] = []
                            if(!excludeResultsFromCacheUp) {
                                locallyUpdatedData = await this.getLocallyUpdatedCachedObjectsAsS();
                            }

                            return this.offlineDataFetcher.searchAll(
                                query, 
                                locallyCreatedData, 
                                locallyUpdatedData, 
                                onlyOnLocallyCreated, 
                                query.language
                            )
                        }
                    ),
                    switchMap(
                        data => { 
                            return from(data)
                        }
                    ),
                    //this.mergeUpdateCacheDataToSearchResults.bind(this)
                );
    }

    /**
     * Performs a simple text search  and returns the first result
     * Useful to search on exact item identifier for example
     * @param queryText 
     */
    public searchOneWithText(queryText: string, types: string[]): Promise<S> {
        return this.searchAll({
            queryText: queryText,
            types: types,
            page: 0,
        })
            .pipe(
                pluck('allData'),
                first(),
                map(
                    searchResults => searchResults[0]
                )
            )
            .toPromise()
    }

    private getSearchTitleTypeFromSearchType(): TitleSearchType[] {
        return this.typesForSearch.map(type => {
            return SEARCH_TITLE_TYPES[type] ?? type
        })
    }

    public searchTitles(searchTitleOptions: SearchTitleOptions) {
        const options = {...searchTitleOptions}

        if(!options.language) {
            options.language = FALLBACK_LOCALE
        }
        if(!options.pageNumber) {
            options.pageNumber = 0
        }
        if(!options.size) {
            options.size = 10
        }
        if(!options.defaultFilter) {
            options.defaultFilter = ""
        }
        if(!options.types) {
            options.types = this.getSearchTitleTypeFromSearchType()
        }
        if(!options.filterText) {
            options.filterText = ""
        }
        options.searchOnVersioned = false

        if(window.navigator.onLine) {
            return this.proxy.fetch("/indexSearch/title", options, "POST")
        } else {
            return from(this.offlineDataFetcher.searchTitles(
                options.types, 
                options.filterText, 
                options.pageNumber, 
                options.defaultFilter, 
                options.size, 
                options.filterIds, 
                options.esPath, 
                options.language
            ))
        }
    }

    public searchTitle(id: string, types?: string[], language: string = FALLBACK_LOCALE) {

        const options: SearchTitleOptions = {
            types: types?.length ? types : this.getSearchTitleTypeFromSearchType(),
            language: language,
            filterIds: [id],
            pageNumber: 0,
            size: 1,
        }
        
        return this.searchTitles(options)
    }

    public updateOfflineMode(id: string, body: Partial<T>) {
        return this.fetchOneOfflineMode(id).pipe(
            switchMap(entity => {
                if(entity) {
                    body = this.mergeOverridingArrays(entity, body) as Partial<T>
                }
                (body as Partial<T> & {id: string}).id = id;
                // to allow sorting on updated date
                _.set(body, 'managementData.updatedDate', getCurrentDateAsSkinDate())
                return from(this.offlineDataUpdateCacher.storeBeforePushing(
                    id, 
                    body as Partial<T> & {id: string}
                ))
            }),
            switchMap(() => this.fetchOneOfflineMode(id))
        );
    }

    public mergeOverridingArrays(baseObject: Object, updates: Object) {
        const util = (baseValue, updatedValue) => {
            if(_.isArray(baseValue) && _.isArray(updatedValue)) {
                return updatedValue
            }
        }

        return _.mergeWith(baseObject, updates, util)
    }

    public update(id: string, body: Partial<T>, endpointOverride?: string, ignoreCachedStatus: boolean = false) {
        return from(this.hasCachedUpdates(id)).pipe(
            switchMap((hasCachedChanges: boolean) => {

                // If online, update directly in backend
                if (window.navigator.onLine && (!hasCachedChanges || ignoreCachedStatus)) { // differentiate locally created entities from other entities
                    // Online update
                    const endpoint = endpointOverride || this.endpoint;
                    const uri = `${endpoint}/${id}`;
                    
                    if(_.get(body,'associatedMedias.medias', []) === null){
                        _.set(body,'associatedMedias.medias', [])
                    }
                    const localMedia = (_.get(body,'associatedMedias.medias', []) as string[])
                        .filter(r => r.startsWith("local:"))
                    const uploadedMedia = (_.get(body,'associatedMedias.medias', []) as string[])
                        .filter(r => !r.startsWith("local:"))
                    _.set(body,'associatedMedias.medias', uploadedMedia)
                    delete (body as any).associatedMedias.illustratedNote

                    return this.proxy
                        .fetch(uri, undefined, 'PUT', body, true)
                        .pipe(
                            switchMap(response => this.fetchOne(id)),
                            /*// Remove corresponding in updateCache (if exists) to avoid two versions of the entity
                            tap(async data => {
                                if(!doNotDeletedCachedUpdates) {
                                    const cachedUpdatedValue = await this.offlineDataUpdateCacher.getCachedValue(id)
                                    if (cachedUpdatedValue) {
                                        this.offlineDataUpdateCacher.removeCachedValue(id)
                                    }
                                    localMedia.forEach(element => {
                                        _.get(body,'associatedMedias.medias', []).push(element)
                                    });
                                    // update cached data from fetch to avoid to versions of the entitiy
                                    this.offlineDataFetcher.updateOne(id, data as T);
                                }
                            }),*/
                            switchMap(() => this.fetchOne(id, true)));
                }
                // If offline, store in cache and return cached data merged with updated data
                else {
                    return this.updateOfflineMode(id, body);
                }
                
            })
        )
    }

    public create(createBody?: Partial<T>, body?: Partial<T>, endpointOverride?: string): Observable<T> {
        const endpoint = endpointOverride ? endpointOverride : `${this.endpoint}`
        if (window.navigator.onLine || !body) {
            // CREATE_THEN_UPDATE entities never accept a body at creation time: createBody can only
            // ever take effect via a follow-up update, so it's merged with body and always
            // applied that way (even when body alone is empty).
            if (this.createPattern === CREATE_PATTERNS.CREATE_THEN_UPDATE) {
                const mergedBody = lodashMerge({}, createBody, body)
                return this.proxy.fetch(this.endpoint, undefined, 'POST', undefined).pipe(
                    concatMap(response => {
                        if (_.isEmpty(mergedBody)) {
                            return of(response);
                        }
                        // In case of a locally created entity, replacing temporary id by the one from db
                        const id: string | undefined = _.get(mergedBody, "id")
                        if (id && OfflineDataFetcher.isTemporaryId(id)) {
                            Object.defineProperty(mergedBody, "id", { value: response.id })
                        }
                        return this.update(response.id, lodashMerge(response, mergedBody));
                    }),
                    switchMap(({ id }) => this.fetchOne(id)),
                );
            }

            const uri = this.createPattern === CREATE_PATTERNS.CREATE_WITH_BODY
                ? `${endpoint}/createWithBody`
                : endpoint;

            return this.proxy.fetch(uri, undefined, 'POST', createBody).pipe(
                concatMap(response => {
                    if (!body) {
                        return of(response);
                    }
                    // In case of a locally created entity, replacing temporary id by the one from db
                    const id: string | undefined = _.get(body, "id")
                    if (id && OfflineDataFetcher.isTemporaryId(id)) {
                        Object.defineProperty(body, "id", { value: response.id })
                    }
                    return this.update(response.id, lodashMerge(response, body));
                }),
                switchMap(({ id }) => this.fetchOne(id)),
            );
        } else { // Offline mode
            // the '_' prefix of the id identifies the id as temporary in the cache keys
            return this.createOffline(createBody, body);
        }
    }

    public createOffline(createBody?: Partial<T>, body?: Partial<T>) {
        const tempId = '_' + Math.random().toString(36).substr(2, 9) + Math.random().toString(36).substr(2, 9);
        const mergedBody = lodashMerge({}, createBody, body)
        const newBody: Partial<T> & {id: string} = Object.assign({id: ''}, mergedBody);
        newBody.id = tempId;

        // to allow sort on creation and update date
        _.set(newBody, 'managementData.creationDate', getCurrentDateAsSkinDate())
        _.set(newBody, 'managementData.updatedDate', getCurrentDateAsSkinDate())

        return from(this.offlineDataUpdateCacher.storeBeforePushing(tempId, newBody)).pipe(
            switchMap(itemId => {
                if (!itemId) {
                    // logger
                    throw new Error('Item not yet available')
                }
                return this.fetchOneOfflineMode(tempId)
            }),
        )
    }

    public duplicate(entityToDuplicateId: string, createBody?: Partial<T>, body?: Partial<T>) {

        const duplicateUri = `${this.endpoint}/duplicate/${entityToDuplicateId}`

        return this.proxy.fetch(duplicateUri, undefined, 'POST', createBody).pipe(
            concatMap(response => {
                if (!body) {
                    return of(response);
                }

                let bodyCopy = _.cloneDeep(body)
                delete (bodyCopy as any).id
                delete (bodyCopy as any).managementData
                delete (bodyCopy as any)._links
                
                return this.update(response.id, this.mergeOverridingArrays(response, bodyCopy) as T);
            }),
            switchMap(({ id }) => this.fetchOne(id))
        );
    }

    /**
     * 
     * @param body Is updated during the execution of this method
     * @param localAssociatedMedias Is updated during the execution of this method
     * @returns An updated version (copy) of localMedias
     */
    private async handleLocalPrincipalsMedias(id: string, body: T & { associatedMedias: AssociatedMedias }, localMedias: string[], localAssociatedMedias: AssociatedMedias) {
        let oldAssociatedMedias: AssociatedMedias | null = null
        if(!OfflineDataFetcher.isTemporaryId(id)) {
            let entityBeforeUpdate: T | null = null
            try{
                entityBeforeUpdate = await this.fetchOneOnlineMode(id).toPromise()
            } catch(err) {
                entityBeforeUpdate = null
            }
            oldAssociatedMedias = (entityBeforeUpdate as any)?.associatedMedias ?? null
        }

        const principalMediaKeys = ['first', 'second', 'third']
        for(const key of principalMediaKeys) {
            if(body.associatedMedias[key] && body.associatedMedias[key].startsWith('local:')) {
                localMedias.unshift(body.associatedMedias[key])

                localAssociatedMedias[key] = body.associatedMedias[key]

                // replacing principal media with old principal media to avoid modifying entity with an illegal state
                if(oldAssociatedMedias && oldAssociatedMedias[key]) {
                    body.associatedMedias[key] = oldAssociatedMedias[key]
                } else {
                    body.associatedMedias[key] = undefined
                }
            }
        }

        return _.uniq(localMedias)
    }

    public hasAssociatedMedias(item): item is {associatedMedias: AssociatedMedias} {
        return !!item.associatedMedias?.medias
    }

    public async getObjectInUpdateCache(id: string) {
        if(this.offlineDataUpdateCacher) {
            return this.offlineDataUpdateCacher.getCachedValue(id)
        } else {
            return Promise.resolve(undefined)
        }
    }

    public async hasCachedUpdates(id: string): Promise<boolean> {
        const res = await this.getObjectInUpdateCache(id) != null
        return res
    }

    public async removeObjectFromUpdateCache(id: string) {
        return this.offlineDataUpdateCacher.removeCachedValue(id)
    }

    public deleteModifications(id: string) {
        return from(this.offlineDataUpdateCacher.fetchOneFromId(id)).pipe(map(e => {
            if(_.get(e, 'associatedMedias.medias',)) {
                (_.get(e, 'associatedMedias.medias') as string[])
                    .filter(media => media.startsWith('local'))
                    .forEach(media => {
                        switch(MediaTransferHelper.getInstance().getMediaType(media)) {
                            case MediaType.IMAGE:
                                ImageTransferHelper.getInstance().deleteImageInCache(media)
                                break;
                        }
                    })
            }

            return this.offlineDataUpdateCacher.deleteBeforePushing(id);
        }))
    }

    public delete(id: string) {
        if (OfflineDataFetcher.isTemporaryId(id)) {
            return this.deleteModifications(id);
        }
        const uri = `${this.endpoint}/${id}`;

        const network$ = this.proxy.fetch(uri, undefined, "DELETE", undefined, undefined).pipe(
            last(),
            catchError(error => (fetch.isNetworkError(error) ? EMPTY : throwError(error))),
        );
        return network$.pipe(distinctUntilChanged(isEqual));
    }

    public fetchOneFromPredicate(predicate: (data: T) => boolean) {
        if (window.navigator.onLine) {
            throw new Error("This method is not meant to be called in an online context");
        } else {
            return this.offlineDataFetcher.fetchOneFromPredicate(predicate)
        }
    }

    public fetchAllFromPredicate(predicate: (data: T) => boolean) {
        if (window.navigator.onLine) {
            throw new Error("This method is not meant to be called in an online context");
        } else {
            return this.offlineDataFetcher.fetchAllFromPredicate(predicate)
        }
    }

    public recursiveGenericMerge(base, newFields) {
        if (!base) {
            return newFields
        }
        if (!newFields) {
            return base
        }
        if (typeof base !== 'object' || typeof newFields !== 'object') {
            return newFields || base
        }
        let keys = Object.keys(newFields)
        Object.keys(base).forEach((e) => keys.push(e))
        keys = _.uniq(keys)
        const mergeResult = {}
        for (const field of keys) {
            if ((base[field] && Array.isArray(base[field])) || (newFields[field] && Array.isArray(newFields[field]))) {
                mergeResult[field] = _.zip(base[field], newFields[field]).map(e => this.recursiveGenericMerge(...e))
            } else if ((base[field] && typeof base[field] === 'object') || (newFields[field] && typeof newFields[field] === 'object')) {
                mergeResult[field] = this.recursiveGenericMerge(base[field], newFields[field])
            } else {
                mergeResult[field] = newFields[field] || base[field]
            }
        }
        return mergeResult
    }

    public addImage(element, image) {
        return this.proxy.fetch(`${this.endpoint}/addMedia/${element.id}`,{idMedia: image.newMediaId}, 'PUT')
    }

    public updateFirstMedia(entityId: string, mediaId: string): Observable<T> {
        return this.fetchOneOnlineMode(entityId).pipe(
            switchMap(entity => {
                let associatedMedias: AssociatedMediasResource = {}
                if((entity as any).associatedMedias) {
                    associatedMedias = (entity as any).associatedMedias
                }

                const updatedAssociatedMedias = MediaTransferHelper.permutateFirstMedia(mediaId, associatedMedias as AssociatedMedias)

                return this.update(entityId, {...entity, associatedMedias: updatedAssociatedMedias})
            }),
            catchError(err => {
                return throwError(new FailedToUpdateFirstMediaError())
            }),
        )
    }

    // tslint:disable-next-line:member-ordering
    protected convertTtoS(toConvert: T): S {
        return toConvert as any as S;
    }

    // tslint:disable-next-line:member-ordering
    protected convertStoT(toConvert: S): T {
        return toConvert as any as T;
    }

    protected abstract initializeDataFetcher()

    protected abstract initializeDataPusher()

    protected mergeUpdateCacheDataToOneResult(source$: Observable<T>) {
        return source$.pipe(switchMap(
            // retrieving cache data that has not been pushed to keep the vue up to date
            async (data: any) => {
                let result = data
                let cachedUpdatedValue: Partial<T> | undefined
                if(this.offlineDataUpdateCacher) {
                    cachedUpdatedValue = await this.offlineDataUpdateCacher.getCachedValue(data.id)
                }
                if (cachedUpdatedValue) {
                    result = lodashMerge(data, cachedUpdatedValue)
                }
                return result
            }
        ))
    }

    /**
     * Returns an the locally created entities as an array
     */
    private async getLocallyCreatedCachedObjectsAsS(): Promise<S[]> {
        let locallyCreatedEntities: T[] = []
        if(this.offlineDataUpdateCacher) {
            locallyCreatedEntities = (await this.offlineDataUpdateCacher.getLocallyCreatedCachedObjects()) as T[] 
        }
        return locallyCreatedEntities.map(this.convertTtoS)
    }

    private async getLocallyUpdatedCachedObjectsAsS(): Promise<S[]> {
        if(this.offlineDataUpdateCacher) {
            const locallyCachedObjects = (await this.offlineDataUpdateCacher.getLocallyUpdatedCachedObjects()) as T[]
            return locallyCachedObjects.map(this.convertTtoS)
        }
        return [] as S[]
    }

    /**
     * Returns an observable emitting the locally created entities as an array
     */
    private locallyCreatedEntitiesToData$(): Observable<S[]> {
        return from(this.getLocallyCreatedCachedObjectsAsS())
    }

    /**
     * Pipeable operator thats merges data in the update cache that have not been pushed yet
     * into the search result (either from sync-cache or directly network result)
     * Only for results of a search request
     * @param source$ 
     */
    private mergeUpdateCacheDataToSearchResults(source$: Observable<SearchResponse<S>>) {
        return source$.pipe(switchMap(
            // retrieving cache data that has not been pushed to keep the vue up to date
            async (data: SearchResponse<S>) => {
                const results = data.allData;
                if (results) {
                    for (let result of results) {
                        let cachedUpdatedValue: Partial<T> | undefined
                        if(this.offlineDataUpdateCacher) {
                            cachedUpdatedValue = await this.offlineDataUpdateCacher.getCachedValue(_.get(result, 'id'))
                        }
                        if (cachedUpdatedValue) {
                            result = this.mergeCacheToEntity(result, cachedUpdatedValue)
                        }
                    }
                }
                return data
            }
        ))
    }

    private mergeCacheToEntity(entity: any, cachedValue: any) {
        const cachedCurrentState = _.get(cachedValue, 'workflowSchema.currentState')
        const mergedResult = lodashMerge(entity, cachedValue)

        if (cachedCurrentState) {
            _.set(mergedResult, 'workflowSchema.currentState', cachedCurrentState);
        }

        return mergedResult;
    }
}
